/**
 * Tetrish - UI Component
 * Handles UI elements, displays, and interactions
 */

import { LINE_CLEAR_ANIM_MS, SCORE_UPDATE_ANIM_MS, LEVEL_UP_ANIM_MS } from '../constants.js';

export class UI {
	constructor(gameState, scoreManager) {
		this.state = gameState;
		this.scoreManager = scoreManager;

		// UI elements
		this.nextPieceDisplay = document.getElementById('next-piece');
		this.holdPieceDisplay = document.getElementById('hold-piece');
		this.scoreElement = document.getElementById('score');
		this.levelElement = document.getElementById('level');
		this.linesElement = document.getElementById('lines');
		this.startBtn = document.getElementById('start-btn');
		this.pauseBtn = document.getElementById('pause-btn');
		this.gameOverlay = document.getElementById('game-overlay');
		this.overlayTitle = document.getElementById('overlay-title');
		this.overlayMessage = document.getElementById('overlay-message');
		this.overlayButton = document.getElementById('overlay-button');
		this.settingsModal = document.getElementById('settings-modal');
	}

	/**
	 * Initialize UI elements
	 */
	initUI() {
		this.hideOverlay();
		this.updateGameState(this.state);
		this.clearPreviewDisplays();
		this.applyStoredSettings();
	}

	/**
	 * Apply persisted settings on startup (before the modal is ever opened)
	 */
	applyStoredSettings() {
		const settings = this.loadSettingsFromStorage();

		// Restore high contrast mode immediately
		if (settings.highContrast) {
			document.documentElement.classList.add('high-contrast');
		}

		// Notify other components (e.g. SoundManager) of persisted values
		window.dispatchEvent(new CustomEvent('settings-changed', { detail: settings }));
	}

	/**
	 * Update UI based on game state
	 */
	updateGameState(gameState) {
		if (gameState.isGameRunning) {
			this.startBtn.textContent = 'Restart Game';
			this.pauseBtn.disabled = false;
			this.pauseBtn.textContent = gameState.isPaused ? 'Resume' : 'Pause';
		} else {
			this.startBtn.textContent = 'Start Game';
			this.pauseBtn.disabled = true;
			this.pauseBtn.textContent = 'Pause';
		}
	}

	/**
	 * Update the score display
	 */
	updateScore(score) {
		this.scoreElement.textContent = score;
		this.scoreElement.classList.add('animate-score-update');

		setTimeout(() => {
			this.scoreElement.classList.remove('animate-score-update');
		}, SCORE_UPDATE_ANIM_MS);
	}

	/**
	 * Update the level display
	 */
	updateLevel(level) {
		this.levelElement.textContent = level;
		this.levelElement.classList.add('animate-level-up');

		setTimeout(() => {
			this.levelElement.classList.remove('animate-level-up');
		}, LEVEL_UP_ANIM_MS);
	}

	/**
	 * Update the lines cleared display
	 */
	updateLines(lines) {
		this.linesElement.textContent = lines;
	}

	/**
	 * Update the high score display
	 */
	updateHighScore(highScore) {
		const highScoreElement = document.getElementById('high-score');
		if (highScoreElement) {
			highScoreElement.textContent = highScore;
			highScoreElement.classList.add('animate-score-update');

			setTimeout(() => {
				highScoreElement.classList.remove('animate-score-update');
			}, SCORE_UPDATE_ANIM_MS);
		}
	}

	/**
	 * Update the next piece preview display
	 */
	updateNextPieceDisplay(piece) {
		if (!piece || !this.nextPieceDisplay) return;

		this.clearDisplay(this.nextPieceDisplay);

		const shape = piece.getCurrentShape();
		const type = piece.type;

		this.renderPieceInDisplay(shape, type, this.nextPieceDisplay);
	}

	/**
	 * Update the hold piece display
	 */
	updateHeldPieceDisplay(piece) {
		if (!this.holdPieceDisplay) return;

		this.clearDisplay(this.holdPieceDisplay);

		if (piece) {
			const shape = piece.getCurrentShape();
			const type = piece.type;

			this.renderPieceInDisplay(shape, type, this.holdPieceDisplay);
		}
	}

	/**
	 * Render a piece shape in a display element
	 */
	renderPieceInDisplay(shape, type, displayElement) {
		// Clear any existing content first
		this.clearDisplay(displayElement);

		// Create a container for the piece that we can center
		const pieceContainer = document.createElement('div');
		pieceContainer.style.position = 'relative';
		pieceContainer.style.width = '80%';
		pieceContainer.style.height = '80%';
		pieceContainer.style.margin = 'auto';

		// Calculate the grid for this piece
		const gridSize = Math.max(shape.length, shape[0].length);
		const cellSize = 100 / gridSize; // Size as percentage

		// Render each cell of the piece
		shape.forEach((row, y) => {
			row.forEach((value, x) => {
				if (value) {
					const block = document.createElement('div');
					block.classList.add('tetromino', `tetromino-${type}`);
					block.style.position = 'absolute';
					block.style.width = `${cellSize}%`;
					block.style.height = `${cellSize}%`;
					block.style.top = `${y * cellSize}%`;
					block.style.left = `${x * cellSize}%`;

					pieceContainer.appendChild(block);
				}
			});
		});

		// Add the piece container to the display element
		displayElement.appendChild(pieceContainer);
	}

	/**
	 * Clear a display element
	 */
	clearDisplay(displayElement) {
		if (!displayElement) return;
		displayElement.innerHTML = '';
	}

	/**
	 * Clear both preview displays
	 */
	clearPreviewDisplays() {
		this.clearDisplay(this.nextPieceDisplay);
		this.clearDisplay(this.holdPieceDisplay);
	}

	/**
	 * Show the game overlay
	 */
	showOverlay(title, message, buttonText = 'Continue') {
		if (!this.gameOverlay) return;

		console.log(`Showing overlay: "${title}"`);

		// Set content
		this.overlayTitle.textContent = title;
		this.overlayMessage.textContent = message;
		this.overlayButton.textContent = buttonText;

		// Show and animate
		this.gameOverlay.hidden = false;
		this.gameOverlay.style.display = 'flex';
		this.gameOverlay.style.pointerEvents = 'auto';
		this.gameOverlay.classList.remove('animate-modal-out');
		this.gameOverlay.classList.add('animate-modal-in');
	}

	/**
	 * Hide the game overlay
	 */
	hideOverlay() {
		if (!this.gameOverlay) return;

		console.log('Hiding game overlay');

		// Set display properties immediately to prevent interaction
		this.gameOverlay.style.pointerEvents = 'none';

		// Add animation class
		this.gameOverlay.classList.add('animate-modal-out');
		this.gameOverlay.classList.remove('animate-modal-in');

		setTimeout(() => {
			this.gameOverlay.hidden = true;
			this.gameOverlay.style.display = 'none';
			this.gameOverlay.classList.remove('animate-modal-out');
			console.log('Game overlay hidden completely');
		}, LINE_CLEAR_ANIM_MS);
	}

	/**
	 * Toggle the settings modal
	 */
	toggleSettingsModal() {
		if (!this.settingsModal) return;

		// Get the current state of the modal
		const isHidden = this.settingsModal.hidden;

		if (isHidden) {
			// Show the modal
			this.settingsModal.hidden = false;
			this.settingsModal.classList.add('animate-modal-in');
			this.settingsModal.classList.remove('animate-modal-out');

			// Load current settings
			this.loadSettings();
		} else {
			// Hide the modal - using a new method to ensure proper cleanup
			this.hideSettingsModal();
		}
	}

	/**
	 * Hide the settings modal
	 */
	hideSettingsModal() {
		if (!this.settingsModal) return;

		// Ensure the element is hidden immediately
		this.settingsModal.hidden = true;

		// Remove animation classes
		this.settingsModal.classList.remove('animate-modal-in');
		this.settingsModal.classList.add('animate-modal-out');

		// Additional CSS to ensure it doesn't block interaction
		this.settingsModal.style.display = 'none';
		this.settingsModal.style.pointerEvents = 'none';

		setTimeout(() => {
			this.settingsModal.classList.remove('animate-modal-out');
		}, LINE_CLEAR_ANIM_MS);
	}

	/**
	 * Load current settings into the settings form
	 */
	loadSettings() {
		const volumeSlider = document.getElementById('volume-slider');
		const musicToggle = document.getElementById('music-toggle');
		const sfxToggle = document.getElementById('sfx-toggle');
		const highContrastToggle = document.getElementById('high-contrast');

		// Load settings from localStorage if available
		const settings = this.loadSettingsFromStorage();

		if (volumeSlider) volumeSlider.value = settings.volume;
		if (musicToggle) musicToggle.checked = settings.music;
		if (sfxToggle) sfxToggle.checked = settings.sfx;
		if (highContrastToggle) highContrastToggle.checked = settings.highContrast;
	}

	/**
	 * Save settings from the form
	 * @returns {Object} The saved settings
	 */
	saveSettings() {
		const volumeSlider = document.getElementById('volume-slider');
		const musicToggle = document.getElementById('music-toggle');
		const sfxToggle = document.getElementById('sfx-toggle');
		const highContrastToggle = document.getElementById('high-contrast');

		const settings = {
			volume: volumeSlider ? parseInt(volumeSlider.value) : 50,
			music: musicToggle ? musicToggle.checked : true,
			sfx: sfxToggle ? sfxToggle.checked : true,
			highContrast: highContrastToggle ? highContrastToggle.checked : false
		};

		// Apply high contrast mode
		if (settings.highContrast) {
			document.documentElement.classList.add('high-contrast');
		} else {
			document.documentElement.classList.remove('high-contrast');
		}

		// Save to localStorage
		this.saveSettingsToStorage(settings);

		// Dispatch event for other components to react to settings changes
		window.dispatchEvent(new CustomEvent('settings-changed', { detail: settings }));

		// Return settings object so calling code can use it
		return settings;
	}

	/**
	 * Load settings from localStorage
	 */
	loadSettingsFromStorage() {
		const defaultSettings = {
			volume: 50,
			music: true,
			sfx: true,
			highContrast: false
		};

		try {
			const storedSettings = localStorage.getItem('tetrish-settings');
			return storedSettings ? JSON.parse(storedSettings) : defaultSettings;
		} catch (e) {
			console.error('Error loading settings:', e);
			return defaultSettings;
		}
	}

	/**
	 * Save settings to localStorage
	 */
	saveSettingsToStorage(settings) {
		try {
			localStorage.setItem('tetrish-settings', JSON.stringify(settings));
		} catch (e) {
			console.error('Error saving settings:', e);
		}
	}
}
